import { Role } from "../constants/role";

/**
 * The shared role model of the UI. Root, Admin, Unit Owner and User views all ask it what the
 * logged-in user may do, instead of checking roles themselves.
 *
 * It only decides what the UI shows and which pages open. The API checks every request on its own;
 * hiding something here is never the protection.
 */

/** Permission levels, highest first. A user's level is the highest that applies to them. */
export const Level = {
	root: "root", // Platform (BasedOn) user: creates organizations, sees no organization content
	admin: "admin", // Organization admin
	unitOwner: "unitOwner", // Admin of at least one unit
	user: "user",
};

/**
 * What the UI knows about a user's roles. userUnits maps unit id => the user's roles in that unit.
 */
export function describeUser(user, userUnits) {
	const unitRoles = user ? userUnits ?? {} : {};
	const isRoot = Boolean(user?.isRoot);
	const isOrgAdmin = Boolean(user?.isAdmin) && !isRoot;
	const isAnyUnitAdmin = Object.values(unitRoles).some((roles) => roles?.includes(Role.admin));
	const isUnitMember = Object.keys(unitRoles).length > 0;
	const level = !user ? null : isRoot ? Level.root : isOrgAdmin ? Level.admin : isAnyUnitAdmin ? Level.unitOwner : Level.user;

	return {
		user: user ?? null,
		level,
		isRoot,
		isOrgAdmin,
		isAdmin: Boolean(user?.isAdmin),
		isUnitAdminOf: (unitId) => Boolean(unitRoles[unitId]?.includes(Role.admin)),
		isAnyUnitAdmin,
		// Member or unit admin of at least one unit
		isUnitMember,
		// Users see the decisions shared with them. Organization admins administer and only work with
		// decisions when they are also a unit admin; root never sees decisions.
		seesDecisions: Boolean(user) && !isRoot && (!isOrgAdmin || isAnyUnitAdmin),
	};
}

/**
 * Every permission the UI checks, as a function of describeUser()'s result (and an argument such
 * as a unit id, where the permission is about one thing). Routes, menu items and controls name
 * these instead of checking roles.
 */
export const PERMISSIONS = {
	// Create organizations and their admins
	manageOrganizations: (u) => u.isRoot,
	// The admin panel: organization admins, and root for platform settings
	adminPanel: (u) => u.isAdmin,
	// Users and organization roles of the organization
	manageUsers: (u) => u.isOrgAdmin,
	createUnits: (u) => u.isOrgAdmin,
	// Edit a unit, its members and its unit admins
	manageUnit: (u, unitId) => u.isOrgAdmin || u.isUnitAdminOf(unitId),
	// An organization's units, people and single decisions (the API decides which decisions);
	// everyone but root
	viewOrganization: (u) => Boolean(u.user) && !u.isRoot,
	// The decision list and search
	viewDecisions: (u) => u.seesDecisions,
	// Decisions are created inside a unit the creator is a member of
	createDecisions: (u) => u.isUnitMember,
	// Meetings and the calendar of the user's units
	unitWork: (u) => u.isUnitMember,
};

export function can(described, permission, ...args) {
	const check = PERMISSIONS[permission];
	if (!check) throw new Error(`Unknown permission: ${permission}`);
	return Boolean(check(described, ...args));
}

/** Where the user lands after logging in, and where pages they may not open send them. */
export function homePath(described) {
	if (described.isRoot) return "/organizations";
	return described.seesDecisions ? "/decisions" : "/units";
}
